
import React, { useState } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { 
  Home, Key, Globe, Users, BarChart2, Gift, Wallet2, 
  Settings, BookOpen, MessageSquare, Tag, ChevronDown, ChevronRight, Paintbrush
} from "lucide-react";
import { useNavigate } from "react-router-dom";

interface MenuItemType {
  label: string;
  icon: React.ReactNode;
  link?: string;
  id: string;
  children?: MenuItemType[];
}

const menuItems: MenuItemType[] = [
  {
    label: "Dashboard",
    icon: <Home className="h-5 w-5 mr-2" />,
    link: "/master/dashboard",
    id: "dashboard",
  },
  {
    label: "License Keys",
    icon: <Key className="h-5 w-5 mr-2" />,
    link: "/master/license-keys",
    id: "license-keys",
  },
  {
    label: "Domains",
    icon: <Globe className="h-5 w-5 mr-2" />,
    link: "/master/domains",
    id: "domains",
  },
  {
    label: "Super Admins",
    icon: <Users className="h-5 w-5 mr-2" />,
    link: "/master/super-admins",
    id: "super-admins",
  },
  {
    label: "Users",
    icon: <Users className="h-5 w-5 mr-2" />,
    link: "/master/users",
    id: "users",
  },
  {
    label: "Blog Manager",
    icon: <BookOpen className="h-5 w-5 mr-2" />,
    link: "/master/blog",
    id: "blog",
  },
  {
    label: "Pending Orders",
    icon: <Gift className="h-5 w-5 mr-2" />,
    link: "/master/pending-orders",
    id: "pending-orders",
  },
  {
    label: "Reward Sales",
    icon: <Tag className="h-5 w-5 mr-2" />,
    link: "/master/reward-sales",
    id: "reward-sales",
  },
  {
    label: "Marketing",
    icon: <Paintbrush className="h-5 w-5 mr-2" />,
    id: "marketing",
    children: [
      {
        label: "Banner Templates",
        icon: <Paintbrush className="h-4 w-4 mr-2" />,
        link: "/master/marketing/banner-templates",
        id: "banner-templates",
      },
      {
        label: "AI Banners",
        icon: <Paintbrush className="h-4 w-4 mr-2" />,
        link: "/master/marketing/ai-banner",
        id: "ai-banner",
      },
      {
        label: "Banner Creator",
        icon: <Paintbrush className="h-4 w-4 mr-2" />,
        link: "/master/marketing/banner-creator",
        id: "banner-creator",
      },
      {
        label: "Manage Banners",
        icon: <Paintbrush className="h-4 w-4 mr-2" />,
        link: "/master/marketing/manage-banners",
        id: "manage-banners",
      },
      {
        label: "Create Banner",
        icon: <Paintbrush className="h-4 w-4 mr-2" />,
        link: "/master/marketing/banner-creation",
        id: "banner-creation",
      },
    ],
  },
  {
    label: "Reports",
    icon: <BarChart2 className="h-5 w-5 mr-2" />,
    link: "/master/reseller-sales",
    id: "reseller-sales",
  },
  {
    label: "Reseller CRM",
    icon: <MessageSquare className="h-5 w-5 mr-2" />,
    link: "/master/reseller-crm",
    id: "reseller-crm",
  },
  {
    label: "Settings",
    icon: <Settings className="h-5 w-5 mr-2" />,
    link: "/master/settings",
    id: "settings",
  },
  {
    label: "Knowledge Base",
    icon: <BookOpen className="h-5 w-5 mr-2" />,
    link: "/master/knowledge-base",
    id: "knowledge-base",
  }
];

// Using a darker background with lighter text for better contrast
const sidebarBg = "bg-[#0F3E43]"; // Dark teal background color
const sidebarText = "text-gray-100"; // Light text for contrast
const sidebarHover = "hover:bg-[#1a5259]"; // Slightly lighter teal on hover
const activeItemBg = "bg-[#1a5259]"; // Highlight color for active item

const MasterLicenseSidebar: React.FC<{ activeTab?: string }> = ({ activeTab }) => {
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Toggle submenu expand/collapse
  const toggleSubmenu = (itemId: string) => {
    setExpandedItems(prev => 
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Improved function to handle menu item clicks
  const handleMenuItemClick = (event: React.MouseEvent<HTMLButtonElement>, link: string) => {
    event.preventDefault();
    event.stopPropagation(); // Stop event propagation
    console.log("Navigating to:", link);
    navigate(link);
  };

  // Function to check if a menu item is active
  const isMenuItemActive = (item: MenuItemType) => {
    // Check if the activeTab prop matches the item's id
    if (activeTab === item.id) {
      return true;
    }
    // Check if any child is active
    if (item.children) {
      return item.children.some(child => activeTab === child.id);
    }
    return false;
  };

  // Render menu items recursively
  const renderMenuItem = (item: MenuItemType) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const isActive = isMenuItemActive(item);
    
    return (
      <SidebarMenuItem key={item.id}>
        {hasChildren ? (
          <>
            <SidebarMenuButton
              isActive={isActive}
              className={`${isActive ? activeItemBg : sidebarHover} w-full`}
              onClick={() => toggleSubmenu(item.id)}
            >
              <div className="w-full flex items-center justify-between">
                <div className="flex items-center">
                  {item.icon}
                  <span>{item.label}</span>
                </div>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </div>
            </SidebarMenuButton>
            
            {isExpanded && item.children && (
              <div className="ml-6 mt-1 space-y-1 border-l-2 border-gray-600 pl-2">
                {item.children.map(child => (
                  <SidebarMenuButton
                    key={child.id}
                    isActive={activeTab === child.id}
                    className={`${activeTab === child.id ? activeItemBg : sidebarHover} w-full`}
                    onClick={(event) => child.link && handleMenuItemClick(event, child.link)}
                  >
                    <div className="flex items-center">
                      {child.icon}
                      <span className="text-sm">{child.label}</span>
                    </div>
                  </SidebarMenuButton>
                ))}
              </div>
            )}
          </>
        ) : (
          <SidebarMenuButton
            asChild
            isActive={isActive}
            className={isActive ? activeItemBg : sidebarHover}
          >
            <button
              type="button"
              className={`w-full flex items-center ${sidebarText} p-2 rounded-md transition-colors`}
              onClick={(event) => item.link && handleMenuItemClick(event, item.link)}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          </SidebarMenuButton>
        )}
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar className={`${sidebarBg} ${sidebarText} min-h-screen flex flex-col border-r border-gray-700`}>
      <SidebarContent className="flex-grow">
        <SidebarGroup>
          <SidebarGroupLabel>
            <span className="font-bold text-lg tracking-wide px-2 pb-4 block text-white">
              ReferMe <span className="text-blue-200">Master</span>
            </span>
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map(renderMenuItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};

export default MasterLicenseSidebar;
